import { Typography } from '@mui/material';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { QueryToSqlHelper } from '../../../../../api/query-to-sql-helper';
import { getTranslation } from '../../../../../constants/systemValuesTranslation';
import { chartMutedTextOnLight } from '../../../../../theme/chart-ui';
import { getAliasForMetricGroupId } from '../../../../api/alias/utils';
import {
  useGetTranslatedMetricGroupName,
  useGetTranslatedMetricIdName,
} from '../../../../api/metrics/hooks';
import { MetricDetails } from '../../../../api/metrics/types';
import { MetricResult } from '../../../../api/types';
import { MetricId } from '../../../../api/types-graphql';
import {
  getMetricTypeFromMetrics,
  metricIdNeedsEmptyCohortFilter,
  toMetricResultOrError,
} from '../../../../api/utils';
import {
  useAliasesContext,
  useEffectiveRoleViewAuthorizationServiceContext,
  useGlobalLocaleContext,
  useMetricDetailsMapContext,
  useMetricsQueryServiceContext,
} from '../../../../context/contexts';
import {
  MetricIdTypeArrays,
  SegmentBy,
  TimeSelection,
} from '../../../../types';
import { mergeSegmentations, removeDuplicates } from '../../../utils';
import { LanguageString } from '../../dashboards/types';
import { Filter } from '../../filter/filterbar/types';
import { ItemsState } from '../../filter/types';
import { toSegmentation } from '../../filter/utils';
import { MetricQueryTooltip } from '../../modals/MetricQueryModal';
import { SingleValueTimePeriodTooltip } from '../../modals/SingleValueTimePeriodModal';
import { UserFilterTooltip } from '../../modals/UserFilterModal';
import { TimeSliderState } from '../../timeslider/types';
import { TitleConfig } from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import {
  ChartTitleProps,
  QueryResponse,
  UseMetricQueriesInputs,
  UseMultipleMetricsQueryInputs,
} from './types';

const getAllFilters = (
  metrics: MetricIdTypeArrays,
  filtersState: ItemsState<Filter>,
  filters: string | null
) => {
  const metricType = getMetricTypeFromMetrics(metrics);
  // Currently, we are assuming that all metrics in the query are of the same type
  // Even without this code below, that implicit assumption is being made
  // TODO Shaq: Improve typing to avoid need for such checks
  const firstMetric = metrics[0];
  const needsEmptyCohortFilter = metricIdNeedsEmptyCohortFilter(
    firstMetric.value as unknown as MetricId
  );
  const sqlFiltersFromFilterState = QueryToSqlHelper.getFilterConditions(
    filtersState.items,
    metricType,
    needsEmptyCohortFilter
  );
  const allFilters =
    [sqlFiltersFromFilterState, filters]
      .flatMap((f) => (f ? [f] : []))
      .join(' AND ') || undefined;
  return allFilters;
};

// TODO Shaq
// Also assuming here that all type(regular, cohort or empCohort) for all
// metrics within the same query is the same. Need to enforce this somehow later.
// For now, I am doing this validateMetrics step just for peace of mind
const validateMetrics = (metrics: MetricIdTypeArrays) => {
  if (!metrics.length) {
    throw new Error('Error: query.metrics array is empty.');
  }
  const uniqueMetricTypes = removeDuplicates(metrics.map((m) => m.type));
  if (uniqueMetricTypes.length > 1) {
    throw new Error(
      `Error: query.metrics contains metrics of different types - ${uniqueMetricTypes.join(' ,')}`
    );
  }
};

const getFinalTimeSelection = (
  timeSliderState: TimeSliderState | null,
  timeSelection: TimeSelection
) => {
  // We use the timeSelection from the query config instead of the timeSliderState
  // so commenting out the timeSliderStateToTimePeriodTimeSelection for now
  // TODO: Figure out how the time slider state has to be used
  return timeSelection;
  // return timeSliderState
  //   ? timeSliderStateToTimePeriodTimeSelection(timeSliderState)
  //   : timeSelection;
};

export const useMetricQueries = (
  inputs: UseMetricQueriesInputs
): {
  queryResponses: QueryResponse[];
} => {
  // TODO: Not sure if we need to pass timeSliderState, segmentationState, filtersState.
  // Maybe they should just be part of the query.
  const { queries, timeSliderState, segmentationState, filtersState } = inputs;
  const metricService = useMetricsQueryServiceContext();
  const queriesToFetch = queries.map((query) => {
    const { metrics, timeSelection, filters, segmentations } = query;
    validateMetrics(metrics);
    const finalTimeSelection = getFinalTimeSelection(
      timeSliderState,
      timeSelection
    );
    const allSegmentations = mergeSegmentations(
      segmentations,
      toSegmentation(
        segmentationState.items,
        getMetricTypeFromMetrics(metrics)
      ),
      metrics,
      2
    );
    const allFilters = getAllFilters(metrics, filtersState, filters ?? null);
    const queryFn = () =>
      toMetricResultOrError(
        metricService,
        metrics,
        finalTimeSelection,
        allFilters,
        allSegmentations
      );
    const queryKey = [
      metricService,
      metrics,
      finalTimeSelection,
      allFilters,
      allSegmentations,
    ];
    return { queryKey, queryFn };
  });

  const results = useSuspenseQueries({
    queries: queriesToFetch,
  });
  const queryResponses: QueryResponse[] = results.map((result) => {
    const { data, error } = result;
    if (error) {
      return { data: null, error };
    } else {
      const firstError = data.find((r) => r instanceof Error) as
        | Error
        | undefined;
      if (firstError) {
        return { data: null, error: firstError };
      } else {
        return {
          data: data as MetricResult[],
          error: null,
        };
      }
    }
  });
  return {
    queryResponses,
  };
};

/**
 * Hook to fetch multiple metrics of the same type in a single query.
 * Use this when all metrics can be combined (same type), avoiding the need
 * for multiple separate queries that useMetricQueries would create.
 */
export const useMultipleMetricsQuery = (
  inputs: UseMultipleMetricsQueryInputs
): {
  queryResponse: QueryResponse;
} => {
  const {
    metrics,
    timeSelection,
    filters,
    segmentations,
    timeSliderState,
    segmentationState,
    filtersState,
  } = inputs;
  const metricService = useMetricsQueryServiceContext();

  validateMetrics(metrics);

  const finalTimeSelection = getFinalTimeSelection(
    timeSliderState,
    timeSelection
  );
  const allSegmentations = mergeSegmentations(
    segmentations,
    toSegmentation(segmentationState.items, getMetricTypeFromMetrics(metrics)),
    metrics,
    2
  );
  const allFilters = getAllFilters(metrics, filtersState, filters ?? null);

  const queryKey = [
    metricService,
    metrics,
    finalTimeSelection,
    allFilters,
    allSegmentations,
  ];
  const queryFn = () =>
    toMetricResultOrError(
      metricService,
      metrics,
      finalTimeSelection,
      allFilters,
      allSegmentations
    );

  const result = useSuspenseQuery({
    queryKey,
    queryFn,
  });

  const { data, error } = result;
  let queryResponse: QueryResponse;

  if (error) {
    queryResponse = { data: null, error };
  } else {
    const firstError = data.find((r) => r instanceof Error) as
      | Error
      | undefined;
    if (firstError) {
      queryResponse = { data: null, error: firstError };
    } else {
      queryResponse = {
        data: data as MetricResult[],
        error: null,
      };
    }
  }

  return {
    queryResponse,
  };
};

/**
 * Helper function to compute the display value for a single metric.
 * Extracted to avoid code duplication between useMetricDisplayValue and useMetricDisplayValues.
 */
const getMetricDisplayValue = (
  metric: MetricId,
  metricDetailsMap: Partial<Record<MetricId, MetricDetails>>,
  getAliasForMetric: ReturnType<typeof getAliasForMetricGroupId>,
  getTranslatedMetricGroupName: ReturnType<
    typeof useGetTranslatedMetricGroupName
  >,
  getTranslatedMetricIdName: ReturnType<typeof useGetTranslatedMetricIdName>
): string => {
  const metricDetails = metricDetailsMap[metric];
  if (!metricDetails) {
    return String(metric);
  }
  const metricGroupAlias = getAliasForMetric(metricDetails.metricGroupId);
  const translatedMetricGroupName = getTranslatedMetricGroupName(
    metricDetails.metricGroupId
  );
  const translatedMetricIdName = getTranslatedMetricIdName(metric);

  return showMetric(
    metricGroupAlias,
    translatedMetricGroupName,
    translatedMetricIdName
  );
};

export const useMetricDisplayValues = (
  metrics: MetricId[]
): Partial<Record<MetricId, string>> => {
  const metricDetailsMap = useMetricDetailsMapContext();
  const locale = useGlobalLocaleContext();
  const aliases = useAliasesContext();
  const getAliasForMetric = getAliasForMetricGroupId(aliases, locale.selected);
  const getTranslatedMetricIdName = useGetTranslatedMetricIdName();
  const getTranslatedMetricGroupName = useGetTranslatedMetricGroupName();

  const metricDisplayValues = Object.fromEntries(
    metrics.map((m) => [
      m,
      getMetricDisplayValue(
        m,
        metricDetailsMap,
        getAliasForMetric,
        getTranslatedMetricGroupName,
        getTranslatedMetricIdName
      ),
    ])
  );
  return metricDisplayValues;
};

export const useMetricDisplayValue = (metric: MetricId): string => {
  const metricDetailsMap = useMetricDetailsMapContext();
  const locale = useGlobalLocaleContext();
  const aliases = useAliasesContext();
  const getAliasForMetric = getAliasForMetricGroupId(aliases, locale.selected);
  const getTranslatedMetricIdName = useGetTranslatedMetricIdName();
  const getTranslatedMetricGroupName = useGetTranslatedMetricGroupName();

  return getMetricDisplayValue(
    metric,
    metricDetailsMap,
    getAliasForMetric,
    getTranslatedMetricGroupName,
    getTranslatedMetricIdName
  );
};

const useChartTitleExtraContent = (
  props: ChartTitleProps
): Pick<TitleConfig, 'extraContent'> => {
  const {
    externalProps,
    metrics,
    filters,
    timeSelection,
    metricsSql,
    segmentations,
  } = props;

  const authorizationService =
    useEffectiveRoleViewAuthorizationServiceContext();
  const canSeeChartDevOptions = authorizationService.canSeeChartDevOptions();
  const { filtersState, timeSliderState } = externalProps;

  const finalTimeSelection = getFinalTimeSelection(
    timeSliderState,
    timeSelection
  );
  const userFilters = getAllFilters(metrics, filtersState, filters ?? null);

  const chartDevContent = (
    <>
      <MetricQueryTooltip metrics={metrics} metricsSql={metricsSql} />
      <UserFilterTooltip userFilters={userFilters} />
    </>
  );

  const extraContent = (
    <>
      {canSeeChartDevOptions && chartDevContent}
      <SingleValueTimePeriodTooltip timeSelection={finalTimeSelection} />
    </>
  );

  return {
    extraContent,
  };
};

export const useChartTitleForGroupByChart = (
  props: ChartTitleProps
): TitleConfig => {
  const { metrics, segmentations } = props;
  const metricDisplayValues = useMetricDisplayValues(
    metrics.map((m) => m.value as unknown as MetricId)
  );
  const { extraContent } = useChartTitleExtraContent(props);
  const { t } = useTranslation();
  const { titleFromConfig } = props;
  const localizedTitle = titleFromConfig?.[props.externalProps.locale.selected];
  if (localizedTitle) {
    return {
      mainTitle: localizedTitle,
      extraContent,
    };
  }
  let mainTitle: string;
  if (metrics.length === 1) {
    mainTitle = showTitleForGroupByChart(
      metricDisplayValues[metrics[0].value] ?? metrics[0].value,
      t,
      undefined,
      segmentations
    );
  } else {
    // TODO: Not implemented for multiple metrics yet
    mainTitle = '';
  }

  return {
    mainTitle,
    extraContent,
  };
};

/**
 * Wrap an existing `TitleConfig` so that a localized subtitle/disclaimer is
 * rendered next to the title via the chart's `extraContent` slot.
 * No-op when `subtitle` is missing or empty for the active locale.
 *
 * Used by container `useConfigToData` hooks (e.g. TimePeriodUnsegmented,
 * OverTimeUnsegmented) to surface ticket disclaimers like
 * "※最新のデータ更新月時点" without stuffing them into the chart title.
 */
export const useTitleConfigWithSubtitle = (
  baseTitleConfig: TitleConfig,
  subtitle: LanguageString | undefined
): TitleConfig => {
  const locale = useGlobalLocaleContext();
  const subtitleText = subtitle?.[locale.selected];
  if (!subtitleText) return baseTitleConfig;
  return {
    ...baseTitleConfig,
    extraContent: (
      <>
        <Typography
          component="span"
          variant="caption"
          sx={{ color: chartMutedTextOnLight, whiteSpace: 'nowrap' }}
        >
          {subtitleText}
        </Typography>
        {baseTitleConfig.extraContent}
      </>
    ),
  };
};

export const useChartTitleForOverTimeChart = (
  props: ChartTitleProps
): TitleConfig => {
  const { metrics, titleFromConfig } = props;
  const metricDisplayValues = useMetricDisplayValues(
    metrics.map((m) => m.value as unknown as MetricId)
  );
  const { extraContent } = useChartTitleExtraContent(props);
  const { t } = useTranslation();
  if (titleFromConfig) {
    return {
      mainTitle: titleFromConfig[props.externalProps.locale.selected],
      extraContent,
    };
  }
  let mainTitle: string;
  if (metrics.length === 1) {
    mainTitle = showTitleForOverTimeChart(
      metricDisplayValues[metrics[0].value] ?? metrics[0].value,
      t
    );
  } else {
    // TODO: Not implemented for multiple metrics yet
    mainTitle = '';
  }
  return {
    mainTitle,
    extraContent,
  };
};

const showTitleForGroupByChart = (
  metricDisplayValue: string,
  t: TFunction,
  title?: string,
  segmentations?: SegmentBy[]
): string => {
  if (title) {
    return title;
  } else {
    const joinedSegmentations = segmentations
      ? segmentations.map((s) => getTranslation(s.dataField)).join(' ')
      : '';
    return t('common:charts.groupByChartTitle', {
      metricDisplayValue,
      joinedSegmentations,
    });
  }
};

const showTitleForOverTimeChart = (
  metricDisplayValue: string,
  t: TFunction,
  title?: string
): string => {
  if (title) {
    return title;
  } else {
    return t('common:charts.overTimeChartTitle', {
      metricDisplayValue,
    });
  }
};

const showMetric = (
  metricGroupAlias: string | null,
  translatedMetricGroup: string,
  translatedMetric: string
) => {
  return `${metricGroupAlias ?? translatedMetricGroup} (${translatedMetric})`;
};
